import { BaseApi } from '@/api/BaseApi'
import { BaseApiParams as BP, CRUDInterface, SortablePositions } from '@/typings/common'

import { TransformerParams } from './TransformerParams'
import { TransformerData } from './TransformerData'


export abstract class Adapter<T, TT = T, API extends BaseApi<T, TT> = BaseApi<T, TT>> implements CRUDInterface {
  public abstract readonly api: API

  public readonly transformerParams = new TransformerParams<T, TT>();

  public readonly transformerData = new TransformerData<T, TT>();

  public getAll<P extends BP = BP, RP extends BP = BP>(payload?: P, rawParams?: RP) {
    const { params, options } = this.transformerParams.getAll(payload, rawParams)

    return this.api.getAll(params, options)
      .then((response) => this.transformerData.getAll(response, payload))
  }

  public getOne<ID extends number|string = number|string, RP extends BP = BP>(id: ID, rawParams: RP) {
    const transformerParams = this.transformerParams.getOne({}, rawParams)
    const { params, options } = transformerParams

    return this.api.getOne(id, params, options)
      .then((response) => this.transformerData.getOne(response, rawParams))
  }

  public create<P extends BP = BP, RP extends BP = BP>(payload: P, rawParams: RP) {
    const { params, options } = this.transformerParams.create(payload, rawParams)

    return this.api.create(params, options)
      .then((response) => this.transformerData.create(response, rawParams))
  }

  public update<ID extends number|string = number|string, P extends BP = BP, RP extends BP = BP>(
    id: ID, payload: P, rawParams: RP,
  ) {
    const transformerParams = this.transformerParams.update(payload, rawParams)
    const { params, options } = transformerParams

    return this.api.update(id, params, options)
      .then((response) => this.transformerData.update(response, rawParams))
  }

  public delete<ID extends number|string = number|string, RP extends BP = BP>(id: ID, rawParams: RP) {
    const transformerParams = this.transformerParams.delete(rawParams)
    const { params, options } = transformerParams

    return this.api.delete(id, params, options)
      .then((response) => this.transformerData.delete(response, rawParams))
  }

  public deleteMultiple(payload: TT[], rawParams?: BP) {
    const transformerParams = this.transformerParams.deleteMultiple(payload, rawParams)
    const { params, options } = transformerParams

    return this.api.deleteMultiple(params, options)
      .then((response) => this.transformerData.deleteMultiple(response, rawParams))
  }

  public updatePositions(payload: SortablePositions, rawParams?: BP) {
    const transformerParams = this.transformerParams.updatePositions(payload, rawParams)
    const { data, options, params } = transformerParams

    return this.api.updatePositions(data, params, options)
      .then((response) => this.transformerData.updatePositions(response, rawParams))
  }
}
